import { useState } from 'react';
import { api } from '../../lib/api';
import { useLiveOrders } from '../../lib/useLiveOrders';
import { useToasts } from '../../lib/toast';
import { useDevice } from '../../store';
import { useLang } from '../../lib/lang';
import { euro } from '../../lib/format';
import { LoginGate } from '../LoginGate';
import type { OrderDto } from '@taj/types';

/** Heure demandée par le client, en HH:MM. */
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const isPaid = (o: OrderDto) => o.paidCents >= o.totalCents;

export function Comptoir() {
  const auth = useDevice((s) => s.auth);
  const resetRole = useDevice((s) => s.resetRole);
  const push = useToasts((s) => s.push);
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const t = (fr: string, en: string) => (lang === 'fr' ? fr : en);
  const typeLabel = (ty: string) =>
    ty === 'DINE_IN' ? '🍽️ ' + t('Sur place', 'Dine in')
    : ty === 'DELIVERY' ? '🛵 ' + t('Livraison', 'Delivery')
    : ty === 'CLICK_COLLECT' ? '📦 ' + t('Click & Collect', 'Click & Collect')
    : '🛍️ ' + t('À emporter', 'Takeaway');

  const [land, setLand] = useState(false); // Portrait (1 écran) / Paysage (max de commandes)

  const { orders } = useLiveOrders('display');

  if (!auth) return <LoginGate mode="pin" />;

  // En paysage : pleine largeur + colonnes plus étroites → un maximum de commandes visibles.
  const gridStyle: React.CSSProperties = land
    ? { maxWidth: 'none', gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))', gap: 10 }
    : {};
  const headW = land ? 'none' : 1100;

  const ready = orders.filter((o) => o.status === 'READY');
  const preparing = orders.filter((o) => o.status === 'RECEIVED' || o.status === 'PREPARING');

  // Libellé + couleur du statut, affiché sur chaque carte.
  const statusInfo = (s: string): { label: string; bg: string; fg: string } =>
    s === 'READY' ? { label: '✅ ' + t('Prête', 'Ready'), bg: '#eafaf0', fg: '#1f9150' }
    : s === 'PREPARING' ? { label: '👨‍🍳 ' + t('En préparation', 'Preparing'), bg: '#fff6e9', fg: '#c67c14' }
    : { label: '📥 ' + t('Reçue', 'Received'), bg: '#eef5fb', fg: '#2a6fb0' };

  const remettre = async (o: OrderDto) => {
    try {
      await api.advanceOrder(o.id); // READY → COMPLETED
      push(`📦 ${o.token ?? '#' + o.number} ${t('remis à', 'handed to')} ${o.clientName}`);
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  /** Carte d'une commande : statut + détail complet (+ bouton « Remis » si prête). */
  const card = (o: OrderDto) => {
    const st = statusInfo(o.status);
    return (
      <div key={o.id} className="ct-card">
        <div className="ct-card-top">
          <span className="ct-token">{o.token ?? '#' + o.number}</span>
          <span className="ct-type">{typeLabel(o.type)}</span>
        </div>
        <div style={{ display: 'inline-block', background: st.bg, color: st.fg, fontWeight: 800, fontSize: '.8rem', padding: '3px 10px', borderRadius: 20, margin: '2px 0 6px' }}>{st.label}</div>
        <div className="ct-name">{o.clientName}</div>
        {o.wantedAt && <div className="cs-wanted">⏰ Pour {hhmm(o.wantedAt)}</div>}
        {o.clientPhone && <div className="cs-contact"><a className="cs-tel" href={`tel:${o.clientPhone}`}>📞 {o.clientPhone}</a></div>}
        {o.deliveryAddress && <div className="ct-addr">📍 {o.deliveryAddress}</div>}

        {/* Détail complet des plats (boissons & desserts inclus). */}
        <div className="ct-detail">
          {(o.lines ?? []).map((l) => (
            <div key={l.id} className="ct-line">
              <span className="ct-qty">{l.qty}×</span>
              <div className="ct-lname">
                <span>{l.nameSnap}</span>
                {(l.options ?? []).length > 0 && (
                  <div className="ct-opts">+ {(l.options ?? []).map((op) => op.nameSnap).join(', ')}</div>
                )}
                {l.notes && <div className="ct-opts" style={{ color: '#c0392b' }}>📝 {l.notes}</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="ct-sum">
          <span className={isPaid(o) ? 'ct-paid' : 'ct-unpaid'}>
            {isPaid(o) ? '✔ ' + t('Payé', 'Paid') : '⚠ ' + t('À régler en caisse', 'To pay at till')}
          </span>
          <span className="ct-total">{euro(o.totalCents)}</span>
        </div>

        {o.status === 'READY'
          ? <button className="ct-btn" onClick={() => remettre(o)}>✓ {t('Remis au client', 'Handed over')}</button>
          : <div style={{ textAlign: 'center', color: '#999', fontSize: '.82rem', padding: '6px 0' }}>⏳ {t('En attente de la cuisine', 'Waiting for kitchen')}</div>}
      </div>
    );
  };

  return (
    <div className="ct">
      <div className="ct-top">
        <span className="ct-ic">🛍️</span>
        <div><div className="cs-staff">STAFF</div><div className="cs-role">{t('Commandes prêtes', 'Ready orders')}</div></div>
        <div style={{ flex: 1 }} />
        <div className="cs-seg" style={{ marginRight: 8 }}>
          <button className={!land ? 'on' : ''} onClick={() => setLand(false)}>📱 {t('Portrait', 'Portrait')}</button>
          <button className={land ? 'on' : ''} onClick={() => setLand(true)}>🖥️ {t('Paysage', 'Landscape')}</button>
        </div>
        <div className="cs-lang">
          <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
      </div>

      <div className="ct-counts">
        <span className="ct-count green">✅ {t('À remettre au client', 'To hand over')} <b>{ready.length}</b></span>
        <span className="ct-count gray">⏳ {t('En attente / préparation', 'Waiting / preparing')} <b>{preparing.length}</b></span>
      </div>

      {/* PRÊTES — à remettre */}
      <div style={{ maxWidth: headW, margin: '10px auto 4px', fontWeight: 900, color: '#1f9150' }}>
        ✅ {t('Prêtes à remettre', 'Ready to hand over')} ({ready.length})
      </div>
      <div className="ct-grid" style={gridStyle}>
        {ready.map(card)}
        {ready.length === 0 && (
          <div className="no-data" style={{ gridColumn: '1/-1', padding: 20 }}>
            {t('Aucune commande prête pour le moment.', 'No order ready yet.')}
          </div>
        )}
      </div>

      {/* EN ATTENTE — encore en cuisine, avec leur statut et le détail */}
      <div style={{ maxWidth: headW, margin: '18px auto 4px', fontWeight: 900, color: '#c67c14' }}>
        ⏳ {t('En attente (cuisine)', 'Waiting (kitchen)')} ({preparing.length})
      </div>
      <div className="ct-grid" style={gridStyle}>
        {preparing.map(card)}
        {preparing.length === 0 && (
          <div className="no-data" style={{ gridColumn: '1/-1', padding: 20 }}>
            {t('Aucune commande en préparation.', 'No order being prepared.')}
          </div>
        )}
      </div>

      <div className="cs-device" onClick={() => confirm(t('Changer le rôle de cet appareil ?', 'Change device role?')) && resetRole()}>⊙ {t('Comptoir', 'Counter')}</div>
    </div>
  );
}
